import React, { useState, useEffect } from 'react';
import { X, Clock, Save } from 'lucide-react';
import { toast } from 'react-toastify';

interface AddDailyHourLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (hourLogData: any) => void;
  programName: string;
  className: string;
  firstName: string;
  lastName: string;
  teacherId: number;
  existingData?: {
    dailyHourLogId?: number;
    clientId?: number;
    teacherId?: number;
    workedOn?: string;
    hoursWorked?: number;
    notes?: string;
    createdOn?: string;
    createdBy?: number;
    updatedOn?: string;
    updatedBy?: number;
  };
}

const AddDailyHourLogModal: React.FC<AddDailyHourLogModalProps> = ({
  isOpen,
  onClose,
  onSave,
  programName,
  className,
  firstName,
  lastName,
  teacherId,
  existingData
}) => {
  const [formData, setFormData] = useState({
    workedOn: '',
    hoursWorked: '',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  // Reset form when modal opens/closes or existingData changes
  useEffect(() => {
    if (isOpen) {
      const today = new Date().toISOString().split('T')[0];
      
      // Fix date conversion to avoid timezone issues
      let workedOnDate = today;
      if (existingData?.workedOn) {
        console.log('Original workedOn value:', existingData);
        console.log('Type of workedOn:', typeof existingData.workedOn);
        
        // If workedOn is already in YYYY-MM-DD format, use it directly
        if (existingData.workedOn.match(/^\d{4}-\d{2}-\d{2}$/)) {
          workedOnDate = existingData.workedOn;
          console.log('Using workedOn as-is (date-only format):', workedOnDate);
        } else {
          // For ISO strings, extract just the date part without timezone conversion
          const dateOnly = existingData.workedOn.split('T')[0];
          workedOnDate = dateOnly;
          console.log('Extracted date part from ISO string:', workedOnDate);
        }
      }
      
      setFormData({
        workedOn: workedOnDate,
        hoursWorked: existingData?.hoursWorked?.toString() || '',
        notes: existingData?.notes || ''
      });
      setErrors({});
    }
  }, [isOpen, existingData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: any) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.workedOn) {
      newErrors.workedOn = 'Work date is required';
    } else {
      const selectedDate = new Date(formData.workedOn);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      
      if (selectedDate > today) {
        newErrors.workedOn = 'Work date cannot be in the future';
      }
    }

    const hoursWorked = parseFloat(formData.hoursWorked);
    if (!formData.hoursWorked || hoursWorked <= 0) {
      newErrors.hoursWorked = 'Hours worked must be greater than 0';
    } else if (hoursWorked > 24) {
      newErrors.hoursWorked = 'Hours worked cannot exceed 24 hours per day';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const hourLogData = {
        dailyHourLogId: existingData?.dailyHourLogId || 0,
        clientId: existingData?.clientId || 1, // Assuming clientId is 1
        teacherId: teacherId,
        workedOn: new Date(formData.workedOn).toISOString(),
        hoursWorked: parseFloat(formData.hoursWorked),
        notes: formData.notes.trim(),
        createdBy: existingData?.createdBy || 1, // Assuming createdBy from user context
        updatedBy: 1 // Assuming updatedBy from user context
      };

      await onSave(hourLogData);
      
      // Close modal immediately
      onClose();
      
    } catch (error) {
      console.error('Error saving hour log:', error);
      // Show error message
      toast.error('Error saving hour log. Please try again.', { 
        autoClose: 3000, 
        theme: "colored" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose}></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg dark:bg-zink-600">
          
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center size-8 bg-blue-100 rounded-md dark:bg-blue-500/20">
                <Clock className="size-4 text-blue-500" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-zink-50">
                {existingData?.dailyHourLogId ? 'Edit Hour Log' : 'Add Hour Log'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-slate-500 hover:text-slate-700 dark:text-zink-300 dark:hover:text-zink-100 disabled:opacity-50"
            >
              <X className="size-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 space-y-4">
            
            {/* Disabled Info Fields */}
            {/* <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                Teacher Name
              </label>
              <textarea
                rows={1}
                value={`${firstName} ${lastName}`}
                disabled
                className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-slate-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-400 resize-none"
              />
            </div> */}

            {/* Editable Hour Log Fields */}
            <div className="pt-0">
              <div className="space-y-4">
                {/* Work Date and Hours Worked side by side */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                      Work Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.workedOn}
                      onChange={(e) => handleInputChange('workedOn', e.target.value)}
                      max={new Date().toISOString().split('T')[0]} // Cannot select future dates
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:text-zink-100 ${
                        errors.workedOn 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-slate-200 dark:border-zink-500'
                      }`}
                      disabled={isLoading}
                    />
                    {errors.workedOn && (
                      <p className="text-red-500 text-xs mt-1">{errors.workedOn}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                      Hours Worked <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        max="24"
                        value={formData.hoursWorked}
                        onChange={(e) => handleInputChange('hoursWorked', e.target.value)}
                        className={`w-full pr-8 pl-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:text-zink-100 ${
                          errors.hoursWorked 
                            ? 'border-red-300 dark:border-red-600' 
                            : 'border-slate-200 dark:border-zink-500'
                        }`}
                        placeholder="0.00"
                        disabled={isLoading}
                      />
                      <span className="absolute right-3 top-2.5 text-slate-500 dark:text-zink-400 text-sm">hrs</span>
                    </div>
                    {errors.hoursWorked && (
                      <p className="text-red-500 text-xs mt-1">{errors.hoursWorked}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Notes
                  </label>
                  <textarea
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100"
                    placeholder="Additional notes about the work done..."
                    disabled={isLoading}
                    maxLength={100}
                  />
                  <p className="text-xs text-slate-500 dark:text-zink-400 mt-1">
                    {formData.notes.length}/100 characters
                  </p>
                </div>

                {/* Last Updated Date - only show if updatedOn exists */}
                {existingData?.updatedOn && (
                  <div className="mt-3 p-3 bg-slate-50 dark:bg-zink-700 rounded-md border border-slate-200 dark:border-zink-500">
                    <div className="flex items-center gap-2">
                      <Clock className="size-4 text-slate-500 dark:text-zink-400" />
                      <span className="text-sm font-medium text-slate-700 dark:text-zink-200">
                        Last Updated:
                      </span>
                      <span className="text-sm text-slate-600 dark:text-zink-300">
                        {(() => {
                          const date = new Date(existingData.updatedOn);
                          const dateStr = date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          });
                          const timeStr = date.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          });
                          return `${dateStr} at ${timeStr}`;
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 dark:border-zink-500">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:text-zink-200 dark:border-zink-500 dark:hover:bg-zink-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="size-4 mr-2" />
                  {existingData?.dailyHourLogId ? 'Update Hour Log' : 'Save Hour Log'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDailyHourLogModal;

